import { useState, useEffect } from "react";
import { Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputVenture } from "@/components/ui/input-venture";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { toast } from "sonner";

export default function AccountSettings() {
  const { role, clientId } = useRole();

  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [timezone, setTimezone] = useState("Pacific Time (PT)");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [updatingPassword, setUpdatingPassword] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setLoading(false);
        return;
      }

      const user = session.user;
      setEmail(user.email || "");

      if (role === "client" && clientId) {
        const { data } = await supabase
          .from('clients')
          .select('contact_name, company_name, phone')
          .eq('id', clientId)
          .single();

        if (data) {
          setFullName(data.contact_name || "");
          setCompanyName(data.company_name || "");
          setPhone(data.phone || "");
        }
      } else if (role === "admin") {
        const { data } = await supabase
          .from('admin_users')
          .select('full_name, email, phone')
          .eq('email', user.email)
          .single();

        if (data) {
          setFullName(data.full_name || "");
          setEmail(data.email || user.email || "");
          setPhone(data.phone || "");
          setCompanyName("Venture Hills");
        }
      }

      setLoading(false);
    };

    fetchUserData();
  }, [role, clientId]);

  const getInitials = () => {
    if (!fullName) return "?";
    return fullName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      if (role === "client" && clientId) {
        const { error } = await supabase
          .from('clients')
          .update({ contact_name: fullName, company_name: companyName, phone })
          .eq('id', clientId);

        if (error) throw error;
      } else if (role === "admin") {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.email) throw new Error("No session");

        const { error } = await supabase
          .from('admin_users')
          .update({ full_name: fullName })
          .eq('email', session.user.email);

        if (error) throw error;
      }
      toast.success("Profile updated successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      toast.success("Password updated successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast.error(error.message || "Failed to update password");
    } finally {
      setUpdatingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle>Profile Information</VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <div className="animate-pulse space-y-4">
              <div className="h-20 w-20 rounded-full bg-muted" />
              <div className="h-10 bg-muted rounded" />
              <div className="h-10 bg-muted rounded" />
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Profile Info */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>Profile Information</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent className="space-y-6">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <span className="text-2xl font-bold text-primary-foreground">{getInitials()}</span>
              </div>
              <button className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-surface-elevated border border-border flex items-center justify-center hover:bg-accent transition-colors">
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <div>
              <p className="font-medium">Profile Photo</p>
              <p className="text-sm text-muted-foreground">JPG, PNG or GIF. Max 2MB.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Full Name</label>
              <InputVenture value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Company Name</label>
              <InputVenture value={companyName} onChange={(e) => setCompanyName(e.target.value)} disabled={role === "admin"} />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Email</label>
            <InputVenture type="email" value={email} disabled />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Phone Number</label>
            <InputVenture type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full h-11 rounded-md border border-input bg-surface px-3 text-sm focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
            >
              <option>Pacific Time (PT)</option>
              <option>Mountain Time (MT)</option>
              <option>Central Time (CT)</option>
              <option>Eastern Time (ET)</option>
            </select>
          </div>

          <Button variant="gradient" className="w-full" onClick={handleSaveProfile} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </VentureCardContent>
      </VentureCard>

      {/* Password */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>Change Password</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Current Password</label>
            <InputVenture type="password" placeholder="--------" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">New Password</label>
            <InputVenture type="password" placeholder="--------" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Confirm New Password</label>
            <InputVenture type="password" placeholder="--------" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          </div>
          <Button variant="gradient-outline" className="w-full" onClick={handleUpdatePassword} disabled={updatingPassword}>
            {updatingPassword ? "Updating..." : "Update Password"}
          </Button>
        </VentureCardContent>
      </VentureCard>
    </div>
  );
}
